import client from './client';

export type ModeFieldType = 'string' | 'enum' | 'date' | 'number';

export interface ModeFieldDefinition {
    name: string;
    type: ModeFieldType;
    label: string;
    required?: boolean;
    enum_values?: string[];
}

export interface ModeStatusDefinition {
    key: string;
    label: string;
    terminal?: boolean;
}

export interface ModeClassificationRule {
    type: 'sender_domain' | 'subject_pattern' | 'entity';
    pattern: string;
    weight: number;
}

export interface ModeSuggestionMigrationPreview {
    eligible_count?: number;
    override_skipped_count?: number;
}

export interface ModeSuggestionEvidence {
    model?: string;
    summary?: string;
    reservation_id?: number;
    subdomain_id?: string;
    migration_preview?: ModeSuggestionMigrationPreview;
    maturity_summary?: string;
    maturity?: Record<string, unknown>;
    migrated_count?: number;
    skipped_override_count?: number;
    migration_warning?: string;
}

export interface ModeSuggestionResponse {
    id: string;
    status: string;
    source: string;
    expires_at: string;
    definition: ModeDefinitionCreatePayload;
    evidence: ModeSuggestionEvidence;
}

export interface PlanImpact {
    custom_mode_limit: number | null;
    active_custom_modes: number;
    custom_modes_remaining: number | null;
    would_exceed_after_approve: boolean;
}

export interface ModeSuggestionExample {
    title: string;
    description: string;
}

export interface ModeSuggestionDetailResponse {
    id: string;
    status: string;
    source: string;
    user_description: string;
    expires_at: string | null;
    definition: ModeDefinitionCreatePayload;
    evidence: ModeSuggestionEvidence;
    examples: ModeSuggestionExample[];
    plan_impact: PlanImpact;
    result_mode_key: string | null;
    created_at?: string | null;
}

export interface ModeSuggestionApproveResponse {
    suggestion_id: string;
    status: string;
    mode: {
        key: string;
        label: string;
        lifecycle_status: string;
        schema_version: number;
    };
}

export interface ModeSuggestionDecisionResponse {
    id: string;
    status: string;
}

export interface ModeDefinitionCreatePayload {
    label: string;
    description?: string;
    icon?: string;
    color?: string;
    fields?: ModeFieldDefinition[];
    statuses?: ModeStatusDefinition[];
    classification_rules?: ModeClassificationRule[];
    extraction_hints?: string;
}

export interface DomainConfig {
    id: number;
    user_id?: number | null;
    key: string;
    label: string;
    icon: string | null;
    color: string | null;
    description?: string | null;
    field_config: Record<string, unknown>;
    extraction_prompt_template?: string | null;
    is_active: boolean;
    is_system_default: boolean;
    sort_order: number;
    lifecycle_status?: 'active' | 'archived' | 'deactivated' | string;
    archived_at?: string | null;
    schema_version?: number;
    fields?: ModeFieldDefinition[];
    statuses?: ModeStatusDefinition[];
    classification_rules?: ModeClassificationRule[];
    extraction_hints?: string;
    created_at?: string;
    updated_at?: string;
}

export interface GetDomainsOptions {
    includeRetired?: boolean;
    signal?: AbortSignal;
}

export const MINIMAL_CUSTOM_MODE_DEFAULTS = {
    fields: [
        { name: 'entity', type: 'string' as const, label: 'Entity', required: true },
        { name: 'status', type: 'string' as const, label: 'Status' },
        { name: 'due_date', type: 'date' as const, label: 'Due Date' },
    ],
    statuses: [
        { key: 'open', label: 'Open' },
        { key: 'done', label: 'Done', terminal: true },
    ],
    classification_rules: [] as ModeClassificationRule[],
    extraction_hints: 'general_v1',
};

export function buildMinimalCustomModePayload(
    label: string,
    options?: { description?: string; icon?: string },
): ModeDefinitionCreatePayload {
    return {
        label: label.trim(),
        description: options?.description?.trim() || undefined,
        icon: options?.icon ?? '✨',
        ...MINIMAL_CUSTOM_MODE_DEFAULTS,
    };
}

export const domainsApi = {
    getDomains: async (options: GetDomainsOptions = {}): Promise<DomainConfig[]> => {
        const response = await client.get('/domains', {
            params: options.includeRetired ? { include_retired: true } : undefined,
            signal: options.signal,
        });
        return response.data;
    },

    getDomain: async (domainKey: string): Promise<DomainConfig> => {
        const response = await client.get(`/domains/${domainKey}`);
        return response.data;
    },

    createDomain: async (payload: ModeDefinitionCreatePayload): Promise<DomainConfig> => {
        const response = await client.post('/domains', payload);
        return response.data;
    },

    archiveMode: async (domainKey: string): Promise<void> => {
        await client.post(`/domains/${encodeURIComponent(domainKey)}/archive`);
    },

    deactivateMode: async (domainKey: string): Promise<void> => {
        await client.post(`/domains/${encodeURIComponent(domainKey)}/deactivate`);
    },

    activateMode: async (domainKey: string): Promise<void> => {
        await client.post(`/domains/${encodeURIComponent(domainKey)}/activate`);
    },

    createModeSuggestion: async (
        description: string,
        options?: { idempotencyKey?: string },
    ): Promise<ModeSuggestionResponse> => {
        const headers: Record<string, string> = {};
        if (options?.idempotencyKey) {
            headers['Idempotency-Key'] = options.idempotencyKey;
        }
        const response = await client.post(
            '/domains/suggestions',
            { description },
            { headers },
        );
        return response.data;
    },

    getModeSuggestion: async (suggestionId: string): Promise<ModeSuggestionDetailResponse> => {
        const response = await client.get(`/domains/suggestions/${encodeURIComponent(suggestionId)}`);
        return response.data;
    },

    approveModeSuggestion: async (
        suggestionId: string,
        options?: { reason?: string },
    ): Promise<ModeSuggestionApproveResponse> => {
        const response = await client.post(
            `/domains/suggestions/${encodeURIComponent(suggestionId)}/approve`,
            options?.reason ? { reason: options.reason } : {},
        );
        return response.data;
    },

    rejectModeSuggestion: async (
        suggestionId: string,
        options?: { reason?: string },
    ): Promise<ModeSuggestionDecisionResponse> => {
        const response = await client.post(
            `/domains/suggestions/${encodeURIComponent(suggestionId)}/reject`,
            options?.reason ? { reason: options.reason } : {},
        );
        return response.data;
    },

    deferModeSuggestion: async (
        suggestionId: string,
        options?: { reason?: string },
    ): Promise<ModeSuggestionDecisionResponse> => {
        const response = await client.post(
            `/domains/suggestions/${encodeURIComponent(suggestionId)}/defer`,
            options?.reason ? { reason: options.reason } : {},
        );
        return response.data;
    },
};

export default domainsApi;
